<template>
  <div class="app-page__container">
    <div v-if="isInvitationLoading" style="position: relative; min-height: 150px;">
      <b-loading :is-full-page="false" :active.sync="isInvitationLoading" :can-cancel="false" />
    </div>
    <div v-else>
      <!-- FORM FOR INVITED USERS -->
      <form
        v-if="activeInvitation && !loggedInUser.id"
        class="sign-up-form"
        @submit.prevent="handleCreateUserWithEmailAndPassword"
      >
        <h4 class="title is-4 has-text-centered">
          Welcome to StandupPro!
        </h4>
        <div class="title is-2 has-text-centered">
          <span role="img" aria-label="hello">👋</span>
          <span class="is-size-6 is-block has-text-weight-normal">
            {{ activeInvitation.inviteeEmail }}
          </span>
        </div>
        <p class="u-p-b">
          {{ activeInvitation.inviterFirstName }} invited you to collaborate!
          <br /><br />
          Sign up and join your team at
          <span class="has-text-weight-bold">
            {{ activeInvitation.workspaceName }}
          </span>
        </p>
        <b-field
          label="Create a password"
          :type="formErrors[signUpForm.userPassword.name] ? 'is-danger' : ''"
          :message="formErrors[signUpForm.userPassword.name] || ''"
        >
          <b-input
            v-model="signUpForm.userPassword.value"
            :name="signUpForm.userPassword.name"
            type="password"
            password-reveal
          />
        </b-field>
        <b-field class="u-m-b-0">
          <b-button
            type="is-primary"
            size="is-large"
            expanded
            @click.prevent="handleCreateUserWithEmailAndPassword"
            :loading="isEmailSignUpInProgress"
          >
            Create Account
          </b-button>
        </b-field>
      </form>

      <!-- STANDARD SIGN UP FORM -->
      <form
        v-else-if="!activeInvitation && !loggedInUser.id"
        class="sign-up-form"
        @submit.prevent="handleCreateUserWithEmailAndPassword"
      >
        <h5 class="title is-5 u-m-t-large">
          Get started with StandupPro for free.
        </h5>
        <p class="subtitle is-size-6">
          Sign up in less than a minute, and give your team superpowers.
        </p>
        <b-field v-if="signUpError || invitationError">
          <b-message type="is-danger">
            {{ signUpError ? signUpError.message : '' }}
            {{ invitationError ? invitationError.message : '' }}
          </b-message>
        </b-field>
        <b-field
          label="Company Email"
          :type="formErrors[signUpForm.userEmail.name] ? 'is-danger' : ''"
          :message="formErrors[signUpForm.userEmail.name] || ''"
        >
          <b-input
            v-model="signUpForm.userEmail.value"
            :name="signUpForm.userEmail.name"
          />
        </b-field>
        <b-field
          label="Password"
          :type="formErrors[signUpForm.userPassword.name] ? 'is-danger' : ''"
          :message="formErrors[signUpForm.userPassword.name] || ''"
        >
          <b-input
            v-model="signUpForm.userPassword.value"
            :name="signUpForm.userPassword.name"
            type="password"
            password-reveal
          />
        </b-field>
        <b-button
          type="is-primary"
          native-type="submit"
          size="is-medium"
          class="is-fullwidth"
          :loading="isEmailSignUpInProgress"
          :disabled="isEmailSignUpInProgress || isGoogleSignUpInProgress"
        >
          Create Account
        </b-button>
        <div class="content-divider" data-content="OR"></div>
        <b-button
          type="is-dark"
          outlined
          class="is-fullwidth"
          @click.prevent="handleLoginWithGoogle"
          :loading="isGoogleSignUpInProgress"
          :disabled="isGoogleSignUpInProgress || isEmailSignUpInProgress"
        >
          <span class="google-logo__container">
            <img
              src="@/assets/images/google-logo-icon-60-by-60.png"
              alt="Google Logo"
              class="google-logo__img"
            />
          </span>
          &nbsp;
          Sign Up With Google
        </b-button>
      </form>
      <onboarding-complete-profile-form
        v-else-if="loggedInUser.id && isUserProfileEmpty"
        class="sign-up-form"
        @submitSuccess="() => $router.push('/dashboard')"
      />
    </div>
    <b-loading is-full-page :active.sync="showLoading" :can-cancel="false"></b-loading>
  </div>
</template>

<script>
import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';
import * as AuthActions from '@/store/actions/Auth.actions';
import * as WorkspaceActions from '@/store/actions/Workspace.actions';
import * as InvitationActions from '@/store/actions/Invitation.actions';
import * as WorkspaceMutations from '@/store/mutations/Workspace.mutations';
import * as ValidationService from '@/services/Validation.service';
import * as InvitationConstants from '@/constants/Invitation.constants';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import { getUserDefaultProfileData } from '@/helpers/userHelpers';
import OnboardingCompleteProfileForm from '../components/OnboardingCompleteProfileForm.vue';

export default {
  name: 'sign-up-view',

  components: {
    OnboardingCompleteProfileForm,
  },

  data() {
    return {
      signUpForm: {
        userEmail: {
          name: 'Email Address',
          value: '',
          required: true,
          validator: ValidationService.validateEmail,
        },
        userPassword: {
          name: 'Password',
          value: '',
          required: true,
        },
      },
      formErrors: {},
      isGoogleSignUpInProgress: false,
      isEmailSignUpInProgress: false,
      isInvitationLoading: true,
      isUserProfileEmpty: false,
      invitationError: false,
    };
  },

  computed: {
    ...mapState({
      showLoading: state => state.auth.showLoading,
      signUpError: state => state.auth.signUpError,
      invitations: state => state.invitation.invitations,
      authUser: state => state.auth.authUser,
    }),
    ...mapGetters([
      'loggedInUser',
    ]),
    accountPayload() {
      return {
        userEmail: this.signUpForm.userEmail.value,
        userPassword: this.signUpForm.userPassword.value,
      };
    },
    activeInvitation() {
      return this.invitations[this.$route.params.invitationId] || null;
    },
    acceptInvitationPayload() {
      const { teamId, workspaceId } = this.activeInvitation;

      return {
        invitationId: this.activeInvitation.id,
        updatedInvitation: { statusId: InvitationConstants.invitationStatusIds.ACCEPTED },
        newUser: { ...getUserDefaultProfileData(this.authUser), workspaceId },
        teamId,
        workspaceId,
      };
    },
  },

  created() {
    this.fetchInvitation();
  },

  watch: {
    '$route.params.invitationId'() { // eslint-disable-line object-shorthand
      this.fetchInvitation();
    },
  },

  methods: {
    ...mapActions({
      createUserWithEmailAndPassword: AuthActions.CREATE_USER_WITH_EMAIL_AND_PASSWORD,
      fetchUserWorkspaces: WorkspaceActions.FETCH_USER_WORKSPACES,
      fetchInvitationById: InvitationActions.FETCH_INVITATION_BY_ID,
    }),
    ...mapMutations({
      setActiveWorkspace: WorkspaceMutations.SET_ACTIVE_WORKSPACE,
    }),
    async fetchInvitation() {
      if (!this.$route.params.invitationId) {
        this.isInvitationLoading = false;
      } else {
        try {
          await this.fetchInvitationById({ invitationId: this.$route.params.invitationId });

          if (!this.activeInvitation || this.activeInvitation.statusId !== InvitationConstants.invitationStatusIds.PENDING) { // eslint-disable-line max-len
            throw new Error('This invitation has expired');
          }
          this.signUpForm.userEmail.value = this.activeInvitation.inviteeEmail;
          this.isInvitationLoading = false;
        } catch (err) {
          console.log(err); // eslint-disable-line no-console
          this.isInvitationLoading = false;
          this.invitationError = err;
          this.$router.push('/sign-up');
        }
      }
    },
    async handleCreateUserWithEmailAndPassword() {
      this.formErrors = ValidationService.runFormValidation({ ...this.signUpForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isEmailSignUpInProgress = true;
      try {
        await this.createUserWithEmailAndPassword(this.accountPayload);
        if (this.activeInvitation) {
          await this.$store.dispatch(InvitationActions.ACCEPT_INVITATION, { ...this.acceptInvitationPayload });
        }
        if (this.loggedInUser.workspaceId) {
          await this.fetchUserWorkspaces({ userId: this.loggedInUser.id });
          this.setActiveWorkspace({ workspaceId: this.loggedInUser.workspaceId });
          if (!this.loggedInUser.firstName || !this.loggedInUser.lastName) {
            this.isUserProfileEmpty = true;
          } else {
            this.$router.push('/dashboard');
          }
        } else {
          this.$router.push('/onboarding');
        }
        this.isEmailSignUpInProgress = false;
      } catch {
        this.isEmailSignUpInProgress = false;
      }
    },
    handleLoginWithGoogle() {
      this.isGoogleSignUpInProgress = true;
      this.$store.dispatch(AuthActions.LOG_IN_WITH_GOOGLE_AUTH);
    },
  },
};
</script>

<style lang="scss" scoped>
.sign-up-form {
  margin: 0 auto;
  @media screen and (min-width: $tablet) {
    max-width: 500px;
  }
}
</style>
